'use client';
import { useState } from 'react';
import { supabaseBrowser } from '@/lib/supabaseBrowser';

export default function FeatureRequestModal({ onClose }: { onClose: () => void }) {
  const supabase = supabaseBrowser();
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit() {
    if (!message.trim()) return;
    setSubmitting(true);
    setError(null);

    // Login is optional -- if there's a session, attach it so the request is
    // tied to that user; if not, submit anonymously rather than blocking.
    const { data: sessionData } = await supabase.auth.getSession();
    const token = sessionData?.session?.access_token;

    try {
      const headers: Record<string, string> = { 'Content-Type': 'application/json' };
      if (token) headers.Authorization = `Bearer ${token}`;

      const res = await fetch('/api/feature-request', {
        method: 'POST',
        headers,
        body: JSON.stringify({ message }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || 'Something went wrong');
      }
      setSubmitted(true);
    } catch (err: any) {
      setError(err.message || 'Something went wrong. Try again in a moment.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 100,
        background: 'rgba(0,0,0,0.5)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '20px',
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: 'var(--surface)',
          border: '1px solid var(--border)',
          borderRadius: 'var(--radius)',
          padding: '28px',
          maxWidth: '400px',
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          gap: '14px',
          position: 'relative',
        }}
      >
        <button
          onClick={onClose}
          aria-label="Close"
          style={{
            position: 'absolute', top: '14px', right: '14px',
            background: 'none', border: 'none', cursor: 'pointer',
            color: 'var(--text-dim)', fontSize: '16px', lineHeight: 1,
            fontFamily: 'DM Sans, sans-serif',
          }}
        >
          ✕
        </button>

        {submitted ? (
          <>
            <h2 style={{ fontFamily: 'Instrument Serif, serif', fontSize: '22px', color: 'var(--text)' }}>
              Thanks!
            </h2>
            <p style={{ fontSize: '13px', color: 'var(--text-muted)', lineHeight: 1.6 }}>
              Got it — we read every suggestion, and yours genuinely helps shape what gets built next during beta.
            </p>
          </>
        ) : (
          <>
            <div>
              <h2 style={{ fontFamily: 'Instrument Serif, serif', fontSize: '22px', color: 'var(--text)', marginBottom: '6px' }}>
                Got an idea?
              </h2>
              <p style={{ fontSize: '13px', color: 'var(--text-muted)', lineHeight: 1.6 }}>
                We're in beta — tell us what would make NoiseGate better for you.
              </p>
            </div>

            <textarea
              value={message}
              onChange={e => setMessage(e.target.value)}
              placeholder="I'd love to see..."
              aria-label="Feature suggestion"
              rows={4}
              maxLength={2000}
              style={{
                background: 'var(--surface-raised)',
                border: '1px solid var(--border)',
                borderRadius: 'var(--radius-sm)',
                padding: '10px 12px',
                fontSize: '13px',
                color: 'var(--text)',
                fontFamily: 'DM Sans, sans-serif',
                resize: 'vertical',
              }}
            />

            {error && (
              <div style={{ fontSize: '12px', color: 'var(--danger)' }}>{error}</div>
            )}

            <button
              onClick={handleSubmit}
              disabled={submitting || !message.trim()}
              style={{
                fontSize: '13px', color: 'var(--green)',
                background: 'var(--green-glow)',
                border: '1px solid var(--green-dim)',
                borderRadius: '20px',
                padding: '8px 16px',
                cursor: submitting || !message.trim() ? 'default' : 'pointer',
                opacity: submitting || !message.trim() ? 0.6 : 1,
                fontFamily: 'DM Sans, sans-serif',
                fontWeight: 500,
                alignSelf: 'flex-start',
              }}
            >
              {submitting ? 'Sending…' : 'Send suggestion'}
            </button>
          </>
        )}
      </div>
    </div>
  );
}
